import { useCallback, useState } from 'react';
import { myWeekSchema, type MyWeek } from '@forecastify/shared';
import { MY_WEEK_TOPICS } from '../../live/topics';
import { useLiveInvalidation } from '../../live/use-live';
import { useSession } from '../../session';
import { useFirstLoad } from '../use-first-load';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 16 Oct 2026 · the caller's week of allocations, for two screens.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Kezdőlap's *Allokációim* and Időrögzítés's *A hét összesítése* both read
 * `/dashboards/my-week` — one read, so the two can never disagree about the same week.
 *
 * @param day Any date inside the week to read; the server snaps it to Monday. Kezdőlap passes
 *   today, Időrögzítés the day selected on its strip.
 *
 * ⚠️ **Not fatal.** Both cards sit beside others on their screen, and a failure here must not take
 * those with it — `null` is drawn as the card's empty or hidden state.
 */
export function useMyWeek(day: string): {
  readonly week: MyWeek | null;
  readonly loading: boolean;
} {
  const session = useSession();
  const [week, setWeek] = useState<MyWeek | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) return;
    const response = await session.request(`/dashboards/my-week?today=${day}`).catch(() => null);
    if (response?.ok !== true) return;
    const parsed = myWeekSchema.safeParse(await response.json().catch(() => null));
    if (parsed.success) setWeek(parsed.data);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); the workspace and the day are what actually change. */
  }, [session.workspaceId, day]);

  const loading = useFirstLoad(reload);
  useLiveInvalidation(MY_WEEK_TOPICS, reload);
  return { week, loading };
}
